/**
 * Submenu Carousel Component
 * 
 * This component adds horizontal scrolling functionality to submenu items
 * with next/previous buttons that appear only when content overflows.
 */
export default class SubmenuCarousel {
  constructor(element, APP) {
    // Store references to DOM elements
    this.carousel = element;
    this.container = this.carousel.querySelector('.submenu-carousel__container');
    this.list = this.carousel.querySelector('.submenu-carousel__list');
    this.prevBtn = this.carousel.querySelector('.submenu-carousel__nav--prev');
    this.nextBtn = this.carousel.querySelector('.submenu-carousel__nav--next');

    // State variables
    this.scrollAmount = 200;
    this.hasOverflow = false;

    // Store APP reference
    this.APP = APP;
  }

  init() {
    if (!this.carousel || !this.container || !this.list) {
      return;
    }

    // Check if we have overflow and need navigation
    this.checkForOverflow();

    // Add event listeners
    this.addEventListeners();

    // Initial button state
    this.updateButtonStates();

    // Listen for resize events to recheck overflow
    window.addEventListener('resize', this.onResize.bind(this));

    // Initialize complete
  }

  checkForOverflow() {
    // Compare the width of the content to the container
    this.hasOverflow = this.list.scrollWidth > this.container.clientWidth;
    // console.log('Submenu Carousel Overflow:', this.hasOverflow);
    // Show/hide navigation buttons based on overflow
    if (this.hasOverflow) {
      this.nextBtn.classList.add('is-visible');
      this.prevBtn.classList.add('is-visible');
    } else {
      this.nextBtn.classList.remove('is-visible');
      this.prevBtn.classList.remove('is-visible');
    }
  }

  addEventListeners() {
    // Button click handlers
    this.nextBtn.addEventListener('click', this.scrollNext.bind(this));
    this.prevBtn.addEventListener('click', this.scrollPrev.bind(this));

    // Scroll event to update button states
    this.list.addEventListener('scroll', this.onScroll.bind(this));
  }

  scrollNext() {
    if (!this.hasOverflow) return;

    // Calculate the amount to scroll
    // Either the default amount or the remaining distance to the end
    const remainingDistance = this.list.scrollWidth - (this.list.scrollLeft + this.container.clientWidth);
    const scrollDistance = Math.min(this.scrollAmount, remainingDistance);

    this.list.scrollBy({
      left: scrollDistance,
      behavior: 'smooth'
    });
  }

  scrollPrev() {
    if (!this.hasOverflow) return;

    // Calculate the amount to scroll
    // Either the default amount or the remaining distance to the start
    const scrollDistance = Math.min(this.scrollAmount, this.list.scrollLeft);

    this.list.scrollBy({
      left: -scrollDistance,
      behavior: 'smooth'
    });
  }

  onScroll() {
    this.updateButtonStates();
  }

  updateButtonStates() {
    if (!this.hasOverflow) return;

    // Calculate if we're at the start or end of the scroll area
    const isAtStart = this.list.scrollLeft <= 1;
    const isAtEnd = Math.abs(this.list.scrollWidth - this.list.scrollLeft - this.container.clientWidth) < 1;

    // Update button states
    if (isAtStart) {
      this.prevBtn.classList.add('is-disabled');
    } else {
      this.prevBtn.classList.remove('is-disabled');
    }

    if (isAtEnd) {
      this.nextBtn.classList.add('is-disabled');
    } else {
      this.nextBtn.classList.remove('is-disabled');
    }
  }

  onResize() {
    // Debounce resize event
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.checkForOverflow();
      this.updateButtonStates();
    }, 150);
  }
}
